import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import { createTooltipProps } from '../utils/fieldTooltips';
import { ValidationRules, validateForm, hasErrors } from '../utils/validation';

function NominalCodeModal({ nominalCode, onClose, onSave }) {
  const [formData, setFormData] = useState({
    type: 'S',
    code: '',
    name: '',
    name_on_invoice: false,
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (nominalCode) {
      setFormData(nominalCode);
    }
  }, [nominalCode]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const newFormData = {
      ...formData,
      [e.target.name]: value,
    };
    setFormData(newFormData);

    // Clear validation errors for this field when user starts typing
    if (validationErrors[e.target.name]) {
      const newErrors = { ...validationErrors };
      delete newErrors[e.target.name];
      setValidationErrors(newErrors);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    const errors = validateForm(formData, ValidationRules.nominalCode);

    if (hasErrors(errors)) {
      setValidationErrors(errors);
      return;
    }

    // Clear any existing errors
    setValidationErrors({});
    onSave(formData);
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-credit-card me-2 text-primary"></i>
          {nominalCode ? 'Edit Nominal Code' : 'Add New Nominal Code'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Type *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('type')}>
                      Type of nominal code for accounting purposes
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    isInvalid={!!validationErrors.type}
                    {...createTooltipProps('type')}
                  >
                    <option value="S">S - Sales</option>
                    <option value="P">P - Purchase</option>
                    <option value="PL">PL - Profit & Loss</option>
                    <option value="BS">BS - Balance Sheet</option>
                  </Form.Select>
                </OverlayTrigger>
                {validationErrors.type && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.type}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Code *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('code')}>
                      Unique identifier for the nominal code
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    maxLength="20"
                    required
                    isInvalid={!!validationErrors.code}
                    {...createTooltipProps('code')}
                  />
                </OverlayTrigger>
                {validationErrors.code && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.code}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip {...createTooltipProps('name')}>
                  Descriptive name for the nominal code
                </Tooltip>
              }
            >
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength="60"
                required
                isInvalid={!!validationErrors.name}
                {...createTooltipProps('name')}
              />
            </OverlayTrigger>
            {validationErrors.name && (
              <Form.Control.Feedback type="invalid">{validationErrors.name}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip {...createTooltipProps('name_on_invoice')}>
                  Controls whether the name appears on invoices
                </Tooltip>
              }
            >
              <Form.Check
                type="checkbox"
                name="name_on_invoice"
                label="Name on Invoice"
                checked={formData.name_on_invoice}
                onChange={handleChange}
                {...createTooltipProps('name_on_invoice')}
              />
            </OverlayTrigger>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            <i className="fas fa-times me-2"></i>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            <i className={`fas ${nominalCode ? 'fa-save' : 'fa-plus'} me-2`}></i>
            {nominalCode ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function NominalCodes() {
  const [nominalCodes, setNominalCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingNominalCode, setEditingNominalCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchNominalCodes = async (page = 1, search = '', type = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/nominal-codes', {
        params: {
          page,
          limit: pagination.limit,
          search,
          type,
        },
      });
      setNominalCodes(response.data.nominalCodes);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch nominal codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNominalCodes();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchNominalCodes(1, searchTerm, typeFilter);
  };

  const handlePageChange = (newPage) => {
    fetchNominalCodes(newPage, searchTerm, typeFilter);
  };

  const handleAddNominalCode = () => {
    setEditingNominalCode(null);
    setShowModal(true);
  };

  const handleEditNominalCode = (nominalCode) => {
    setEditingNominalCode(nominalCode);
    setShowModal(true);
  };

  const handleSaveNominalCode = async (nominalCodeData) => {
    try {
      if (editingNominalCode) {
        await axios.put(`/api/nominal-codes/${editingNominalCode.id}`, nominalCodeData);
        setSuccess('Nominal code updated successfully');
      } else {
        await axios.post('/api/nominal-codes', nominalCodeData);
        setSuccess('Nominal code created successfully');
      }
      setShowModal(false);
      fetchNominalCodes(pagination.page, searchTerm, typeFilter);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save nominal code';
      setError(errorMessage);

      // If it's a validation error (duplicate, length, etc.), don't close the modal so user can fix it
      if (
        errorMessage.toLowerCase().includes('already exists') ||
        errorMessage.toLowerCase().includes('must be') ||
        errorMessage.toLowerCase().includes('missing required')
      ) {
        // Keep modal open for validation errors
      } else {
        setShowModal(false);
      }
    }
  };

  const handleDeleteNominalCode = async (id) => {
    if (window.confirm('Are you sure you want to delete this nominal code?')) {
      try {
        await axios.delete(`/api/nominal-codes/${id}`);
        setSuccess('Nominal code deleted successfully');
        fetchNominalCodes(pagination.page, searchTerm, typeFilter);
      } catch (error) {
        setError('Failed to delete nominal code');
      }
    }
  };

  const handleDeleteAllNominalCodes = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL nominal codes? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your nominal code records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/nominal-codes');
          setSuccess(
            `All nominal codes deleted successfully (${response.data.deletedCount} records)`
          );
          fetchNominalCodes(1, '', '');
        } catch (error) {
          setError('Failed to delete all nominal codes');
        }
      }
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'S':
        return 'Sales';
      case 'P':
        return 'Purchase';
      case 'PL':
        return 'Profit & Loss';
      case 'BS':
        return 'Balance Sheet';
      default:
        return type;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'S':
        return '#28a745';
      case 'P':
        return '#007bff';
      case 'PL':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i
                  className="bi bi-calculator-fill me-3 text-primary"
                  style={{ fontSize: '2rem' }}
                ></i>
                <div>
                  <h1 className="mb-1">Nominal Codes</h1>
                  <p className="text-muted mb-0">
                    Manage your accounting nominal codes and categories
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Button variant="primary" onClick={handleAddNominalCode} className="me-2">
                Add Nominal Code
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAllNominalCodes}
                disabled={nominalCodes.length === 0}
              >
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by code or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Form.Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  style={{ minWidth: '150px' }}
                >
                  <option value="">All Types</option>
                  <option value="S">Sales</option>
                  <option value="P">Purchase</option>
                  <option value="PL">Profit & Loss</option>
                  <option value="BS">Balance Sheet</option>
                </Form.Select>
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
          {loading ? (
            <div className="text-center p-3">Loading nominal codes...</div>
          ) : nominalCodes.length === 0 ? (
            <div className="text-center p-3">No nominal codes found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Name on Invoice</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nominalCodes.map((nominalCode) => (
                      <tr key={nominalCode.id}>
                        <td>
                          <Badge
                            bg={
                              nominalCode.type === 'S'
                                ? 'success'
                                : nominalCode.type === 'P'
                                  ? 'warning'
                                  : nominalCode.type === 'BS'
                                    ? 'secondary'
                                    : 'info'
                            }
                            text={nominalCode.type === 'P' ? 'dark' : 'white'}
                          >
                            {getTypeLabel(nominalCode.type)}
                          </Badge>
                        </td>
                        <td>
                          <strong>{nominalCode.code}</strong>
                        </td>
                        <td className="name">{nominalCode.name}</td>
                        <td>
                          {nominalCode.name_on_invoice ? (
                            <Badge bg="success">✓ Yes</Badge>
                          ) : (
                            <Badge bg="secondary">✗ No</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditNominalCode(nominalCode)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteNominalCode(nominalCode.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <NominalCodeModal
          nominalCode={editingNominalCode}
          onClose={() => setShowModal(false)}
          onSave={handleSaveNominalCode}
        />
      )}
    </Container>
  );
}

export default NominalCodes;
